import { useCallback, useEffect, useRef, useState } from "react";
import { DatabaseZap, Loader2, CheckCircle2, AlertTriangle, ShieldCheck } from "lucide-react";
import { api, apiErr } from "../../lib/api";
import { toast } from "sonner";
import { AdminLayout } from "./AdminLayout";

const PHRASE = "MIGRATE-TO-WSTF";
const card = "rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-5";

function Table({ rows, cols, testid }) {
  return (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-left text-slate-500">{cols.map(([k, l]) => <th key={k} className="py-1.5 pr-4 font-normal uppercase tracking-wider">{l}</th>)}</tr></thead>
        <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[#1d1a10] text-slate-300">{cols.map(([k]) => <td key={k} className="py-1.5 pr-4">{String(r[k] ?? "—")}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function StatusLine({ st }) {
  if (!st) return null;
  const tone = st.state === "APPLIED" ? "text-emerald-400" : st.state === "FAILED" ? "text-rose-400" : "text-[#E5B842]";
  return (
    <div data-testid="idm-status" className="text-sm text-slate-300 flex flex-wrap gap-x-6 gap-y-1">
      <span>State: <b className={tone} data-testid="idm-state">{st.state}</b></span>
      {st.progress && st.state === "RUNNING" && <span data-testid="idm-progress">{st.progress.phase} {st.progress.done}/{st.progress.total} {st.progress.collection || ""}</span>}
      {st.applied_at && <span>Applied: {new Date(st.applied_at).toLocaleString()}</span>}
      {st.backup && <span data-testid="idm-backup">Backup: {st.backup.database} ({st.backup.mode}, {st.backup.complete ? "verified" : "INCOMPLETE"})</span>}
      {st.error && <span className="text-rose-400" data-testid="idm-error">{st.error}</span>}
    </div>
  );
}

function Report({ rep }) {
  const rows = rep.collections.filter((r) => r.needs_rewrite > 0);
  return (
    <div className={`${card} space-y-4`} data-testid="idm-report">
      <div className="text-sm text-slate-200">Documents needing rewrite: <b className="text-[#E5B842]" data-testid="idm-pending">{rep.pending_docs}</b> across {rows.length} collections ({rep.collections.length} scanned). Sample IDs now: {rep.sample_player_ids.join(", ")}</div>
      {rows.length > 0 && <Table rows={rows} cols={[["collection", "Collection"], ["docs", "Docs"], ["needs_rewrite", "Needs rewrite"]]} testid="idm-report-table" />}
      <Table rows={rep.integrity} cols={[["ref", "Reference"], ["distinct_refs", "Refs"], ["resolved", "Resolved"], ["dangling", "Dangling"]]} testid="idm-integrity-table" />
      <ul className="text-xs text-slate-500 list-disc pl-5">{rep.exceptions.map((e) => <li key={e}>Excluded: {e}</li>)}</ul>
    </div>
  );
}

function Verify({ v }) {
  return (
    <div className={`${card} space-y-4`} data-testid="idm-verify">
      <div className={`flex items-center gap-2 text-sm ${v.ok ? "text-emerald-400" : "text-rose-400"}`}>{v.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} Verify: {v.ok ? "OK" : "CHECK"} — leftover documents {v.leftover_docs}. IDs before: {v.sample_ids_before.join(", ")} → after: {v.sample_ids_after.join(", ")}</div>
      <Table rows={v.applied_rows} cols={[["collection", "Collection"], ["docs_before", "Docs before"], ["docs_after", "Docs after"], ["matched", "Matched"], ["written", "Written"]]} testid="idm-applied-table" />
      <Table rows={v.integrity_after} cols={[["ref", "Reference (after)"], ["distinct_refs", "Refs"], ["resolved", "Resolved"], ["dangling", "Dangling"]]} testid="idm-integrity-after-table" />
    </div>
  );
}

export default function AdminIdMigration() {
  const [st, setSt] = useState(null);
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState("");
  const [typed, setTyped] = useState("");
  const poll = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.post("/admin/system/id-migration", { mode: "status" });
    setSt(data);
    if (data.state !== "RUNNING" && poll.current) { clearInterval(poll.current); poll.current = null; }
    return data;
  }, []);
  useEffect(() => { load().catch(() => {}); return () => poll.current && clearInterval(poll.current); }, [load]);

  const dryRun = async () => {
    setBusy("dry"); setRep(null);
    try { const { data } = await api.post("/admin/system/id-migration", { mode: "dry-run" }); setRep(data.report); setSt(data.status); }
    catch (e) { toast.error(apiErr(e, "Dry-run failed")); }
    setBusy("");
  };
  const apply = async () => {
    setBusy("apply");
    try {
      const { data } = await api.post("/admin/system/id-migration", { mode: "apply", confirm: typed });
      setSt(data.status); setTyped("");
      poll.current = setInterval(() => load().catch(() => {}), 1500);
    } catch (e) { toast.error(apiErr(e, "Apply refused")); }
    setBusy("");
  };

  const applied = st?.state === "APPLIED";
  const running = st?.state === "RUNNING";
  return (
    <AdminLayout>
    <div className="space-y-6 max-w-5xl" data-testid="admin-id-migration">
      <div>
        <h1 className="text-2xl font-black uppercase tracking-tight text-white flex items-center gap-2"><DatabaseZap className="w-6 h-6 text-[#E5B842]" /> ID Migration (WSTF)</h1>
        <p className="text-sm text-slate-400 mt-1">One-time switch of stored public IDs and brand text from WSBF to WSTF. A verified backup is taken inside the database cluster before anything is rewritten. Old WSBF- links keep working.</p>
      </div>
      <div className={card}><StatusLine st={st} /></div>
      {!applied && (
        <div className={`${card} space-y-4`}>
          <div className="text-xs font-mono uppercase tracking-widest text-[#E5B842]">Step 1 · Analyze (no changes)</div>
          <button type="button" data-testid="idm-dry-run-btn" onClick={dryRun} disabled={!!busy || running}
            className="inline-flex items-center gap-2 rounded-lg bg-[#E5B842] text-black font-bold text-sm px-4 py-2 disabled:opacity-40">
            {busy === "dry" && <Loader2 className="w-4 h-4 animate-spin" />} Run dry-run
          </button>
        </div>
      )}
      {rep && <Report rep={rep} />}
      {rep && rep.pending_docs > 0 && !applied && (
        <div className={`${card} space-y-3`} data-testid="idm-apply-panel">
          <div className="text-xs font-mono uppercase tracking-widest text-[#E5B842]">Step 2 · Backup → migrate → verify</div>
          <p className="text-sm text-slate-400">Type <b className="text-white font-mono">{PHRASE}</b> to confirm. Valid for 10 minutes after the dry-run.</p>
          <div className="flex flex-wrap gap-3">
            <input data-testid="idm-confirm-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={PHRASE}
              className="rounded-lg bg-black border border-[#2b2512] px-3 py-2 text-sm font-mono text-white w-64" />
            <button type="button" data-testid="idm-apply-btn" onClick={apply} disabled={typed !== PHRASE || !!busy || running}
              className="inline-flex items-center gap-2 rounded-lg border border-rose-500/60 text-rose-300 font-bold text-sm px-4 py-2 disabled:opacity-30">
              {(busy === "apply" || running) && <Loader2 className="w-4 h-4 animate-spin" />} Migrate to WSTF
            </button>
          </div>
        </div>
      )}
      {applied && st.verify && <Verify v={st.verify} />}
      {applied && <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Migration is locked as applied — it cannot run twice.</div>}
    </div>
    </AdminLayout>
  );
}
